import { useTranslation } from "react-i18next";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { isBadgeProvider } from "../lib/identity";

interface ProviderBadgeProps {
  provider: string | null | undefined;
  className?: string;
}

/**
 * The provider badge rendered next to a display name: the provider of the
 * player's most recent sign-in. It disambiguates duplicate display names and
 * is never part of the name itself.
 */
export function ProviderBadge({
  provider,
  className = "",
}: ProviderBadgeProps) {
  const { t } = useTranslation();
  const isDark = useAppStore((state) => state.isDark);
  const theme = getThemeColors(isDark);
  if (!isBadgeProvider(provider)) return null;

  const label = t(`components.providerBadge.${provider}`);

  if (provider === "twitch" || provider === "discord") {
    return (
      <span
        data-testid={`provider-badge-${provider}`}
        role="img"
        aria-label={label}
        title={label}
        className={`inline-flex size-4 shrink-0 items-center justify-center ${theme.textMuted} ${className}`}
      >
        <svg
          aria-hidden="true"
          className="size-3.5"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          {provider === "twitch" ? (
            <path d="M2 2v16h5v4l4-4h5l6-6V2H2Zm18 9-3 3h-5l-3 3v-3H5V4h15v7ZM9 6h2v4H9V6Zm5 0h2v4h-2V6Z" />
          ) : (
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
          )}
        </svg>
      </span>
    );
  }

  return (
    <span
      data-testid={`provider-badge-${provider}`}
      className={`inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] leading-none font-medium uppercase tracking-wide ${theme.badge} ${className}`}
    >
      {label}
    </span>
  );
}
